import type {
  ResolvedToolDefinition,
  ToolCalloutGroup,
  ToolCategoryDefinition,
  ToolFaq,
  ToolIntroBlock,
  ToolReferenceTable,
  ToolStep,
} from "./types";

const EDITORIAL_RULES = {
  discouragedPhrases: {
    "No Signup": "No Account Required",
    "no signup": "no account required",
  },
} as const;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function assertMetadataTextLength(
  value: string,
  label: string,
  min: number,
  max: number,
) {
  const length = value.trim().length;

  if (length < min || length > max) {
    throw new Error(
      `${label} should be between ${min} and ${max} characters. Received ${length}.`,
    );
  }
}

// Expanded stopwords to filter out common SEO "fluff" words
const STOPWORDS = new Set([
  "with",
  "from",
  "your",
  "this",
  "that",
  "how",
  "for",
  "the",
  "and",
  "online",
  "free",
  "best",
]);

export function validateMetadataKeywords(
  ownerLabel: string,
  keywords: readonly string[] | undefined,
) {
  // 1. Core existence check
  if (!keywords || !Array.isArray(keywords) || keywords.length === 0) {
    throw new Error(`${ownerLabel} must define at least one metadata keyword.`);
  }

  // 2. Quantity Cap (Anti-Spam)
  const MAX_KEYWORDS = 12;
  if (keywords.length > MAX_KEYWORDS) {
    throw new Error(
      `${ownerLabel} exceeds the limit of ${MAX_KEYWORDS} keywords. Quality over quantity!`,
    );
  }

  const lowerKeywords: string[] = [];
  const uniqueCheck = new Set<string>();

  // 3. Single-Pass Normalization, Length, and Uniqueness Checks
  for (const rawKeyword of keywords) {
    const k = rawKeyword.trim().toLowerCase();

    if (!k) continue; // Skip accidental empty strings

    if (k.length > 80) {
      throw new Error(
        `${ownerLabel} has a keyword that is too long: "${k.substring(0, 10)}..."`,
      );
    }

    if (k.split(/\s+/).length > 7) {
      throw new Error(
        `${ownerLabel} keyword "${k}" has too many words. Max 7.`,
      );
    }

    if (uniqueCheck.has(k)) {
      throw new Error(`${ownerLabel} has a duplicate metadata keyword: "${k}"`);
    }

    uniqueCheck.add(k);
    lowerKeywords.push(k);
  }

  // 4. Brand Keyword Check (Entity SEO)
  if (!lowerKeywords.some((k) => k.includes("snappy"))) {
    throw new Error(
      `${ownerLabel} must include at least one branded keyword (e.g., "snappy").`,
    );
  }

  // 5. Intent Diversity Check
  checkIntentDiversity(ownerLabel, lowerKeywords);

  // 6. Semantic Stuffing Check
  checkSemanticDensity(ownerLabel, lowerKeywords);
}

/**
 * Ensures keyword intent diversity (checker, converter, tool, etc.)
 */
function checkIntentDiversity(label: string, keywords: string[]) {
  const intents = new Set(
    keywords.map((k) => {
      if (
        k.includes("checker") ||
        k.includes("analyzer") ||
        k.includes("inspector")
      )
        return "analysis";
      if (k.includes("convert") || k.includes("change") || k.includes("maker"))
        return "conversion";
      if (k.includes("tool") || k.includes("app")) return "software";
      return "informational";
    }),
  );

  // If every single keyword maps to the exact same intent category, fail it.
  if (intents.size < 2) {
    throw new Error(
      `${label}: Keywords lack intent diversity. Mix "checker", "converter", "tool", or descriptive terms.`,
    );
  }
}

/**
 * Checks semantic keyword stuffing using word frequency.
 * Safely strips punctuation to accurately count root words.
 */
function checkSemanticDensity(label: string, keywords: string[]) {
  const wordCounts: Record<string, number> = {};

  keywords.forEach((phrase) => {
    // Strip punctuation before splitting to ensure "dpi," and "dpi" are counted identically
    const cleanPhrase = phrase.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
    const uniqueWords = new Set(cleanPhrase.split(/\s+/));

    uniqueWords.forEach((word) => {
      if (!STOPWORDS.has(word) && word.length > 3) {
        wordCounts[word] = (wordCounts[word] || 0) + 1;
      }
    });
  });

  // Limit: A single word cannot appear in more than 65% of the total keywords
  const threshold = keywords.length * 0.65;

  for (const [word, count] of Object.entries(wordCounts)) {
    if (count >= threshold) {
      throw new Error(
        `${label}: The word "${word}" appears in ${count} of ${keywords.length} keywords. This will be flagged as keyword stuffing by Google.`,
      );
    }
  }
}

function assertUniqueStrings(
  values: string[],
  message: (value: string) => string,
) {
  const seen = new Set<string>();

  for (const value of values) {
    if (seen.has(value)) {
      throw new Error(message(value));
    }

    seen.add(value);
  }
}

function validateFaqs(tool: ResolvedToolDefinition, faqs: readonly ToolFaq[]) {
  assertUniqueStrings(
    faqs.map((faq) => faq.question),
    (question) =>
      `Duplicate FAQ question found for tool "${tool.slug}": ${question}`,
  );

  for (const faq of faqs) {
    if (!isNonEmptyString(faq.question) || !isNonEmptyString(faq.answer)) {
      throw new Error(
        `Tool "${tool.slug}" has an FAQ with an empty question or answer.`,
      );
    }
  }
}

function validateSteps(
  tool: ResolvedToolDefinition,
  steps: readonly ToolStep[],
) {
  if (steps.length === 0) {
    throw new Error(
      `Tool "${tool.slug}" declares steps but provides an empty steps array.`,
    );
  }

  for (const step of steps) {
    if (
      !isNonEmptyString(step.number) ||
      !isNonEmptyString(step.title) ||
      !isNonEmptyString(step.description)
    ) {
      throw new Error(`Tool "${tool.slug}" has an incomplete step definition.`);
    }
  }
}

function validateCalloutGroup(
  tool: ResolvedToolDefinition,
  group: ToolCalloutGroup,
) {
  if (!isNonEmptyString(group.title)) {
    throw new Error(`Tool "${tool.slug}" has a callout group without a title.`);
  }

  if (!Array.isArray(group.items) || group.items.length === 0) {
    throw new Error(
      `Tool "${tool.slug}" has an empty callout group "${group.title}".`,
    );
  }

  for (const item of group.items) {
    if (!isNonEmptyString(item.label)) {
      throw new Error(
        `Tool "${tool.slug}" has a callout item with an empty label.`,
      );
    }

    if (item.icon && !isNonEmptyString(item.icon)) {
      throw new Error(
        `Tool "${tool.slug}" has a callout item with an empty icon value.`,
      );
    }
  }
}

function validateReferenceTable(
  tool: ResolvedToolDefinition,
  table: ToolReferenceTable,
) {
  if (!isNonEmptyString(table.title)) {
    throw new Error(`Tool "${tool.slug}" has a reference table without a title.`);
  }

  if (!Array.isArray(table.columns) || table.columns.length === 0) {
    throw new Error(
      `Tool "${tool.slug}" has reference table "${table.title}" without columns.`,
    );
  }

  if (!Array.isArray(table.rows) || table.rows.length === 0) {
    throw new Error(
      `Tool "${tool.slug}" has reference table "${table.title}" without rows.`,
    );
  }

  for (const column of table.columns) {
    if (!isNonEmptyString(column)) {
      throw new Error(
        `Tool "${tool.slug}" has reference table "${table.title}" with an empty column name.`,
      );
    }
  }

  for (const row of table.rows) {
    for (const column of table.columns) {
      if (!isNonEmptyString(row[column])) {
        throw new Error(
          `Tool "${tool.slug}" has reference table "${table.title}" with an empty "${column}" cell.`,
        );
      }
    }
  }
}

function validateIntroBlock(tool: ResolvedToolDefinition, block: ToolIntroBlock) {
  if (block.kind === "text") {
    if (!isNonEmptyString(block.title)) {
      throw new Error(`Tool "${tool.slug}" has a text intro block without a title.`);
    }

    if (!Array.isArray(block.paragraphs) || block.paragraphs.length === 0) {
      throw new Error(
        `Tool "${tool.slug}" has text intro block "${block.title}" without paragraphs.`,
      );
    }

    for (const paragraph of block.paragraphs) {
      if (!isNonEmptyString(paragraph)) {
        throw new Error(
          `Tool "${tool.slug}" has text intro block "${block.title}" with an empty paragraph.`,
        );
      }
    }

    return;
  }

  if (block.kind === "callouts") {
    if (!Array.isArray(block.groups) || block.groups.length === 0) {
      throw new Error(
        `Tool "${tool.slug}" has an intro callouts block without groups.`,
      );
    }

    for (const group of block.groups) {
      validateCalloutGroup(tool, group);
    }

    return;
  }

  if (block.kind === "code") {
    if (!isNonEmptyString(block.title)) {
      throw new Error(`Tool "${tool.slug}" has a code intro block without a title.`);
    }

    if (!isNonEmptyString(block.code)) {
      throw new Error(`Tool "${tool.slug}" has a code intro block without code.`);
    }

    return;
  }

  if (!Array.isArray(block.tables) || block.tables.length === 0) {
    throw new Error(`Tool "${tool.slug}" has an intro table block without tables.`);
  }

  for (const table of block.tables) {
    validateReferenceTable(tool, table);
  }
}

export function assertValidToolRegistry(
  tools: readonly ResolvedToolDefinition[],
  categories: readonly ToolCategoryDefinition[],
) {
  assertUniqueStrings(
    tools.map((tool) => tool.slug),
    (slug) => `Duplicate tool slug found: ${slug}`,
  );

  assertUniqueStrings(
    tools.map((tool) => tool.href),
    (href) => `Duplicate tool href found: ${href}`,
  );

  assertUniqueStrings(
    categories.map((category) => category.slug),
    (slug) => `Duplicate tool category slug found: ${slug}`,
  );

  assertUniqueStrings(
    categories.map((category) => category.href),
    (href) => `Duplicate tool category href found: ${href}`,
  );

  for (const tool of tools) {
    const requiredFields = {
      slug: tool.slug,
      name: tool.name,
      description: tool.description,
      longDescription: tool.longDescription,
      category: tool.category,
      categorySlug: tool.categorySlug,
      href: tool.href,
    } as const;

    for (const [field, value] of Object.entries(requiredFields)) {
      if (!isNonEmptyString(value)) {
        throw new Error(`Tool is missing required field "${field}".`);
      }
    }

    if (tool.href !== `/tools/${tool.slug}`) {
      throw new Error(
        `Tool "${tool.slug}" has href "${tool.href}" but expected "/tools/${tool.slug}".`,
      );
    }

    if (
      !tool.toolType ||
      !isNonEmptyString(tool.toolType.label) ||
      !isNonEmptyString(tool.toolType.icon)
    ) {
      throw new Error(
        `Tool "${tool.slug}" is missing a valid toolType label or icon.`,
      );
    }

    if (!Array.isArray(tool.heroBadges) || tool.heroBadges.length === 0) {
      throw new Error(
        `Tool "${tool.slug}" must define at least one hero badge.`,
      );
    }

    if (!tool.pageContent?.sections?.steps?.title) {
      throw new Error(`Tool "${tool.slug}" must define steps section copy.`);
    }

    if (!tool.pageContent?.sections?.benefits?.title) {
      throw new Error(`Tool "${tool.slug}" must define benefits section copy.`);
    }

    if (!tool.pageContent?.sections?.faqs) {
      throw new Error(`Tool "${tool.slug}" must define faq section copy.`);
    }

    const matchingCategory = categories.find(
      (category) => category.slug === tool.categorySlug,
    );

    if (!matchingCategory) {
      throw new Error(
        `Tool "${tool.slug}" references unknown categorySlug "${tool.categorySlug}".`,
      );
    }

    if (tool.category !== matchingCategory.name) {
      throw new Error(
        `Tool "${tool.slug}" has category "${tool.category}" but categorySlug "${tool.categorySlug}" maps to "${matchingCategory.name}".`,
      );
    }

    for (const [discouraged, preferred] of Object.entries(
      EDITORIAL_RULES.discouragedPhrases,
    )) {
      if (tool.toolType.label.includes(discouraged)) {
        throw new Error(
          `Tool "${tool.slug}" uses discouraged copy "${discouraged}" in toolType.label. Use "${preferred}" instead.`,
        );
      }

      for (const badge of tool.heroBadges) {
        if (badge.includes(discouraged)) {
          throw new Error(
            `Tool "${tool.slug}" uses discouraged copy "${discouraged}" in heroBadges. Use "${preferred}" instead.`,
          );
        }
      }
    }

    assertMetadataTextLength(
      tool.name,
      `Tool "${tool.slug}" metadata title`,
      10,
      120,
    );
    assertMetadataTextLength(
      tool.description,
      `Tool "${tool.slug}" metadata description`,
      50,
      200,
    );
    validateMetadataKeywords(`Tool "${tool.slug}"`, tool.keywords);

    if (tool.pageContent?.faqs) {
      validateFaqs(tool, tool.pageContent.faqs);
    }

    if (tool.pageContent?.intro) {
      if (!isNonEmptyString(tool.pageContent.intro.title)) {
        throw new Error(`Tool "${tool.slug}" has an intro without a title.`);
      }

      if (
        !Array.isArray(tool.pageContent.intro.paragraphs) ||
        tool.pageContent.intro.paragraphs.length === 0
      ) {
        throw new Error(`Tool "${tool.slug}" has an intro without paragraphs.`);
      }

      for (const paragraph of tool.pageContent.intro.paragraphs) {
        if (!isNonEmptyString(paragraph)) {
          throw new Error(
            `Tool "${tool.slug}" has an intro with an empty paragraph.`,
          );
        }
      }
    }

    if (tool.pageContent?.introBlocks) {
      if (
        !Array.isArray(tool.pageContent.introBlocks) ||
        tool.pageContent.introBlocks.length === 0
      ) {
        throw new Error(`Tool "${tool.slug}" has introBlocks but no blocks defined.`);
      }

      for (const block of tool.pageContent.introBlocks) {
        validateIntroBlock(tool, block);
      }
    }

    for (const group of tool.pageContent?.callouts ?? []) {
      validateCalloutGroup(tool, group);
    }

    for (const table of tool.pageContent?.referenceTables ?? []) {
      validateReferenceTable(tool, table);
    }

    for (const benefit of tool.pageContent?.benefits ?? []) {
      if (!isNonEmptyString(benefit.icon)) {
        throw new Error(
          `Tool "${tool.slug}" has a benefit with an empty icon value.`,
        );
      }
    }

    if (tool.pageContent?.steps) {
      validateSteps(tool, tool.pageContent.steps);
    }
  }

  for (const category of categories) {
    const ownerLabel = `Category "${category.slug}"`;

    if (!isNonEmptyString(category.name)) {
      throw new Error(`${ownerLabel} must define a name.`);
    }

    if (!isNonEmptyString(category.slug)) {
      throw new Error(`${ownerLabel} must define a slug.`);
    }

    if (!isNonEmptyString(category.description)) {
      throw new Error(`${ownerLabel} must define a description.`);
    }

    if (!isNonEmptyString(category.longDescription)) {
      throw new Error(`${ownerLabel} must define a longDescription.`);
    }

    if (category.href !== `/tools/${category.slug}`) {
      throw new Error(
        `${ownerLabel} has href "${category.href}" but expected "/tools/${category.slug}".`,
      );
    }

    assertMetadataTextLength(
      category.name,
      `${ownerLabel} metadata title`,
      5,
      80,
    );
    assertMetadataTextLength(
      category.description,
      `${ownerLabel} metadata description`,
      50,
      200,
    );
    validateMetadataKeywords(ownerLabel, category.keywords);
  }
}
